import get from "lodash-es/get";
import { attrToBool } from "src/hoc/repository/algolia/service/algoliaMapperService";
import { i18n } from "src/i18n";
import { Mapping } from "src/types/primitives";

import environment from "../environment";
import { IAddress } from "../types/address";
import { ICatalogProducts, IPrismicProduct } from "../types/prismic";
import { IProduct } from "../types/product";
import { IRelayPoint } from "../types/relayPoints";
import { ITarget2SellProduct } from "../types/target2Sell";

import { getProductAvailabilityLabel } from "./product.service";

export const mapTarget2SellProductToProduct = (
  products: ITarget2SellProduct[]
): IProduct[] => {
  return products.map(
    (product: ITarget2SellProduct): IProduct => {
      const redirectUrl = /http[s]?:\/\/[^/]*(\/.*)/.exec(product.url);
      const parseToNumber = (str: string): number =>
        parseFloat(str.replace(/ /g, ""));
      const price = parseToNumber(product.appliedPrice || "");

      return {
        actualPrice: price,
        displayPrice: !!price,
        detail: product.productType || "",
        id: product.internalId
          ? product.internalId.toString().replace(/ /g, "")
          : "",
        image:
          product.imageUrl && environment.REACT_APP_ENVIRONMENT !== "production"
            ? product.imageUrl.replace(
                "http://devel2.whi.whiskyfr.lmdw.nbs-test.com/",
                "http://whisky.fr/"
              )
            : product.imageUrl || "",
        name: product.name || "",
        originalPrice: price,
        stars: null,
        url: redirectUrl && redirectUrl.length > 1 ? redirectUrl[1] : "",
      };
    }
  );
};

export const getAvailabilityLabel = (
  availabilityLabel?: string | undefined
): string | undefined => {
  if (availabilityLabel === "En stock") {
    availabilityLabel = "Bientôt disponible";
  } else if (availabilityLabel === "In stock") {
    availabilityLabel = "Soon available";
  }

  return availabilityLabel;
};

/**
 * Get attribute
 * @param obj
 * @param attrName
 */
const getAttr = (attr: Mapping, attrName: string): string =>
  attr[i18n.language === "fr" ? attrName : attrName + "_en"] || attr[attrName];

export const catalogProductToProduct = ({
  product_id,
  sku,
  name,
  final_price,
  regular_price,
  short_description,
  image_url,
  url_path,
  stars,
  attributes,
  short_description_en,
}: ICatalogProducts): IProduct => ({
  id: `${product_id}`,
  sku,
  reference: sku,
  name: name[0],
  actualPrice: final_price,
  originalPrice: regular_price,
  displayPrice: attrToBool(attributes.display_price),
  shortDescription:
    (i18n.language !== "fr" && short_description_en) || short_description,
  image: image_url,
  url: url_path,
  stars,
  detail: getAttr(attributes, "product_type"),
  productSubFamily: getAttr(attributes, "product_type"),
  productFamily: getAttr(attributes, "product_family"),
  originCountry: getAttr(attributes, "origin_country"),
  originRegion: getAttr(attributes, "origin_region"),
  strength: attributes.strength,
  volume: attributes.volume,
  isNew: attrToBool(attributes.is_new),
  isIncontournable: attrToBool(attributes.is_incontournable),
  isTopVente: attrToBool(attributes.top_vente),
  isDispoWhiskyLive: attrToBool(attributes.dispo_whisky_live),
  presale: attributes.presale,
  presaleShippingDate: attributes.presale_shipping_date,
  availabilityLabel: getProductAvailabilityLabel(
    attributes.availability_label_new
  ),
});

export const mapPrismicProductToProduct = (
  products: Array<IPrismicProduct | ICatalogProducts>
): IProduct[] =>
  products.map(
    (prismicProduct: IPrismicProduct | ICatalogProducts): IProduct => {
      if ("catalog_products" in prismicProduct) {
        prismicProduct = prismicProduct.catalog_products;
      }

      return catalogProductToProduct(prismicProduct);
    }
  );

export const mapRelayPointsToAddresses = (
  relayPoints: IRelayPoint[]
): IAddress[] =>
  relayPoints.map(
    (relayPoint: IRelayPoint): IAddress => ({
      city: get(relayPoint, "localite", ""),
      company: get(relayPoint, "nomEnseigne", ""),
      country: { id: "FR", name: "France" },
      firstName: "",
      id: get(relayPoint, "identifiantChronopostPointA2PAS", ""),
      isDefaultBilling: false,
      isDefaultShipping: false,
      lastName: "",
      postcode: get(relayPoint, "codePostal", ""),
      street: get(relayPoint, "address1", ""),
      telephone: "",
    })
  );
